import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import tw from 'twin.macro';
import { useSnapshot } from 'valtio';
import { authStore } from '../../../store/authStore';
import { roleStore, Role, roleActions } from '../../../store/roleStore';
import ChatListPage from './ChatListPage';
import NotificationsPage from './NotificationsPage';
import { useGetUnreadCountsQuery } from '../../services/notificationService/__generated__/queries.generated';
import chatService from '../../services/chatService/ChatService';

const Container = styled.div`
  ${tw`min-h-screen bg-white w-full max-w-full min-w-0 box-border`}
`;

const TabContainer = styled.div`
  ${tw`bg-white border-b border-border-default sticky top-0 z-10 w-full max-w-full min-w-0 box-border`}
`;

const TabWrapper = styled.div`
  ${tw`mx-auto w-full max-w-screen-xl min-w-0 box-border px-4 sm:px-6`}
`;

const TabList = styled.div`
  ${tw`flex space-x-8`}
`;

const Tab = styled.button<{ $active: boolean }>`
  ${tw`py-4 px-1 text-sm font-medium border-b-2 border-solid transition-colors flex items-center gap-2 cursor-pointer`}
  ${props =>
    props.$active
      ? tw`border-black text-text-primary`
      : tw`border-transparent text-text-secondary hover:text-text-secondary hover:border-border-default`}
`;

const Badge = styled.span<{ $active: boolean }>`
  ${tw`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold rounded-full max-w-full min-w-0 box-border`}
  ${props => (props.$active ? tw`bg-black text-white` : tw`bg-bg-secondary text-text-secondary`)}
`;

const RoleToggleContainer = styled.div`
  ${tw`flex items-center justify-center gap-4 py-3 px-4 sm:px-6 bg-bg-secondary border-b border-border-default w-full max-w-full min-w-0 box-border`}
`;

const RoleToggleLabel = styled.span`
  ${tw`text-sm font-medium text-text-secondary`}
`;

const RoleToggleWrapper = styled.div`
  ${tw`flex rounded-lg bg-bg-secondary p-0.5`}
`;

const RoleButton = styled.button<{ $active: boolean }>`
  ${tw`px-4 py-1.5 text-sm font-medium rounded-md transition-all`}
  ${props =>
    props.$active
      ? tw`bg-white text-text-primary shadow-xs`
      : tw`text-text-secondary hover:text-text-primary`}
`;

type TabType = 'messages' | 'notifications';

const ChatPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState<TabType>(
    tabParam === 'notifications' ? 'notifications' : 'messages',
  );
  const auth = useSnapshot(authStore);
  const { role: currentRole } = useSnapshot(roleStore);
  const userId = auth.user?.id;
  const isAuthenticated = auth.isAuthenticated && !!userId;

  useEffect(() => {
    roleActions.initRole(auth.isAuthenticated, auth.user);
  }, [auth.isAuthenticated, auth.user]);

  // Fetch unread notification count from backend (filtered by role)
  // Only query when user is authenticated - no polling, FCM triggers refetch
  const chatRole = currentRole === Role.ADMIN ? Role.GUEST : currentRole;

  const { data: unreadData, refetch: refetchUnreadCounts } = useGetUnreadCountsQuery({
    variables: { role: chatRole as string },
    skip: !isAuthenticated,
    fetchPolicy: 'cache-first',
  });

  // Track unread messages count from Firebase chats
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);

  useEffect(() => {
    if (!userId) return;

    // Subscribe to user chats to get total unread count (filtered by role)
    const unsubscribe = chatService.subscribeToUserChats(
      userId,
      chats => {
        const totalUnread = chats.reduce((sum, chat) => sum + (chat.unreadCount || 0), 0);
        setUnreadMessagesCount(totalUnread);
      },
      chatRole as 'guest' | 'host',
    );

    return () => {
      unsubscribe();
    };
  }, [userId, chatRole]);

  const notificationsCount = unreadData?.getUnreadCounts?.notificationsCount || 0;

  const handleRoleChange = (role: Role) => {
    roleActions.setRole(role);
  };

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setSearchParams(tab === 'notifications' ? { tab: 'notifications' } : {});
  };

  // Update active tab when URL param changes
  useEffect(() => {
    if (tabParam === 'notifications' && activeTab !== 'notifications') {
      setActiveTab('notifications');
    } else if (tabParam !== 'notifications' && activeTab !== 'messages') {
      setActiveTab('messages');
    }
  }, [tabParam]);

  return (
    <Container>
      {currentRole !== Role.ADMIN && (
        <RoleToggleContainer>
          <RoleToggleLabel>View Mode:</RoleToggleLabel>
          <RoleToggleWrapper>
            <RoleButton
              $active={currentRole === Role.GUEST}
              onClick={() => handleRoleChange(Role.GUEST)}
            >
              Guest
            </RoleButton>
            <RoleButton
              $active={currentRole === Role.HOST}
              onClick={() => handleRoleChange(Role.HOST)}
            >
              Host
            </RoleButton>
          </RoleToggleWrapper>
        </RoleToggleContainer>
      )}

      <TabContainer>
        <TabWrapper>
          <TabList>
            <Tab $active={activeTab === 'messages'} onClick={() => handleTabChange('messages')}>
              Messages
              {unreadMessagesCount > 0 && (
                <Badge $active={activeTab === 'messages'}>
                  {unreadMessagesCount > 99 ? '99+' : unreadMessagesCount}
                </Badge>
              )}
            </Tab>
            <Tab
              $active={activeTab === 'notifications'}
              onClick={() => handleTabChange('notifications')}
            >
              Notifications
              {notificationsCount > 0 && (
                <Badge $active={activeTab === 'notifications'}>
                  {notificationsCount > 99 ? '99+' : notificationsCount}
                </Badge>
              )}
            </Tab>
          </TabList>
        </TabWrapper>
      </TabContainer>

      {activeTab === 'messages' ? (
        <ChatListPage role={currentRole as 'guest' | 'host'} />
      ) : (
        <NotificationsPage role={currentRole as 'guest' | 'host'} onRefetch={refetchUnreadCounts} />
      )}
    </Container>
  );
};

export default ChatPage;
